import type { ReactNode } from 'react';
import type { GroupOperator } from '@/components/tree-dnd/tree-dnd-types';
import { TreeDndOperatorLabel } from '@/components/tree-dnd/tree-dnd-operator-label';
import { cn } from '@/lib/utils';

interface TreeDndOperatorRailProps {
  operator: GroupOperator;
  children: ReactNode;
}

/**
 * Vertically centers a single group-operator tag to the left of its children.
 * Child rows should use {@link TreeDndOperatorRailBranch} for spine stubs.
 */
export function TreeDndOperatorRail({
  operator,
  children,
}: TreeDndOperatorRailProps) {
  return (
    <div className="flex items-center">
      <div className="flex shrink-0 items-center">
        <TreeDndOperatorLabel operator={operator} />
        <div className="h-px w-2 bg-border" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

type BranchPosition = 'first' | 'middle' | 'last';

interface TreeDndOperatorRailBranchProps {
  position: BranchPosition;
  /**
   * Where the horizontal stub sits vertically within the branch row.
   * Defaults to roughly the vertical center of a condition header.
   */
  stubOffsetClassName?: string;
  children: ReactNode;
}

/**
 * Draws the vertical spine segment and horizontal stub for one child under an
 * operator rail. Sibling gaps between branches should also render a spine
 * continuation via {@link TreeDndOperatorRailSpine}.
 */
export function TreeDndOperatorRailBranch({
  position,
  stubOffsetClassName = 'top-4',
  children,
}: TreeDndOperatorRailBranchProps) {
  return (
    <div className="flex items-stretch">
      <div className="relative w-3 shrink-0">
        <div
          className={cn(
            'absolute left-0 w-px bg-border',
            position === 'first' && 'bottom-0 top-4',
            position === 'middle' && 'inset-y-0',
            position === 'last' && 'top-0 h-4'
          )}
          aria-hidden
        />
        <div
          className={cn(
            'absolute left-0 h-px w-full bg-border',
            stubOffsetClassName
          )}
          aria-hidden
        />
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** Continues the vertical spine through gaps between child branches. */
export function TreeDndOperatorRailSpine({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn('relative w-3 shrink-0', className)} aria-hidden>
      <div className="absolute inset-y-0 left-0 w-px bg-border" />
    </div>
  );
}
